import { DateFields } from '@/src/types/itinerary'
import { Box, FormControl, MenuItem, Select } from '@mui/material'
import { useTranslations } from 'next-intl'
import { FC } from 'react'

interface DateSelectorInputProps {
    dateValue: DateFields
    validDays: number[]
    months: string[]
    years: number[]
    onDateChange: (field: keyof DateFields, value: string) => void
    size?: 'small' | 'medium'
    disabled?: boolean
    onBlur?: () => void
}

const DateSelectorInput: FC<DateSelectorInputProps> = ({
    dateValue,
    validDays,
    months,
    years,
    onDateChange,
    size = 'small',
    disabled = false,
    onBlur
}) => {
    const t = useTranslations('form')
    const tMonths = useTranslations('form.months')

    //send blur event to parent behave of 3 select inputs
    const handleBlur = () => {
        if (dateValue.day && dateValue.month && dateValue.year) {
            onBlur?.()
        }
    }

    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size={size} sx={{ flex: 1 }} disabled={disabled}>
                <Select
                    value={dateValue.day || ''}
                    onChange={e => onDateChange('day', e.target.value)}
                    onBlur={handleBlur}
                    displayEmpty
                    renderValue={selected => selected || t('day')}
                    sx={{
                        borderRadius: 1,
                        fontSize: 14,
                        height: 32,
                        '& .MuiSelect-select': { py: 0.5, px: 1.5 }
                    }}
                    MenuProps={{ PaperProps: { sx: { borderRadius: 1, maxHeight: 240, boxShadow: 3 } } }}
                >
                    {validDays.map(d => (
                        <MenuItem key={d} value={d}>
                            {d}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size={size} sx={{ flex: 1 }} disabled={disabled}>
                <Select
                    value={dateValue.month || ''}
                    onChange={e => onDateChange('month', e.target.value)}
                    onBlur={handleBlur}
                    displayEmpty
                    renderValue={selected => {
                        if (!selected) return t('month')
                        return tMonths(selected.toLowerCase())
                    }}
                    sx={{
                        borderRadius: 1,
                        fontSize: 14,
                        height: 32,
                        '& .MuiSelect-select': { py: 0.5, px: 1.5 }
                    }}
                    MenuProps={{ PaperProps: { sx: { borderRadius: 1, maxHeight: 240, boxShadow: 3 } } }}
                >
                    {months.map((month, index) => (
                        <MenuItem key={month} value={month}>
                            {tMonths(month.toLowerCase())}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size={size} sx={{ flex: 1 }} disabled={disabled}>
                <Select
                    value={dateValue.year || ''}
                    onChange={e => onDateChange('year', e.target.value)}
                    onBlur={handleBlur}
                    displayEmpty
                    renderValue={selected => selected || t('year')}
                    sx={{
                        borderRadius: 1,
                        fontSize: 13,
                        height: 32,
                        '& .MuiSelect-select': { py: 0.5, px: 1.5 }
                    }}
                    MenuProps={{ PaperProps: { sx: { borderRadius: 1, maxHeight: 240, boxShadow: 3 } } }}
                >
                    {years.map(y => (
                        <MenuItem key={y} value={y}>
                            {y}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}

export { DateSelectorInput }
export type { DateSelectorInputProps }
